import React, { useState, useEffect } from "react";
import "@assets/js/common/table";
import AngleContentsAdd from "@assets/js/modals/AngleContentsAdd";
import ThemeAngleSelectionModal from "@pages/design-management/page-design/components/ThemeAngleSelectionModal.jsx";
import designApi from "@services/designApi.jsx";
import themeAngleMappingApi from "@services/themeAngleMappingApi.jsx";
import ResourceUploadField from "@components/common/ResourceUploadField.jsx";

const AngleSetting = ({ themeId, themePageMaster }) => {

    const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;
    const themePageId = themePageMaster.id;

    // 위치 조절을 위한 로컬 상태
    const [leftButtonPosition, setLeftButtonPosition] = useState({
        x: 0,
        y: 0,
    });
    const [rightButtonPosition, setRightButtonPosition] = useState({
        x: 0,
        y: 0,
    });
    const [angleFilter, setAngleFilter] = useState("all");

    const [anglePage, setAnglePage] = useState({
        themePageMaster: themePageMaster,
    });

    // 테마 앵글 매핑 데이터 상태
    const [themeAngleMappings, setThemeAngleMappings] = useState([]);
    const [availableAngles, setAvailableAngles] = useState([]);

    // 전체 선택 상태
    const [selectAll, setSelectAll] = useState(false);

    // 모달 상태 관리 수정
    const [showThemeAngleModal, setShowThemeAngleModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [editTargetMapping, setEditTargetMapping] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // 상세정보 모달 관리
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [currentAngle, setCurrentAngle] = useState(null);

    // 미리보기 모달 관리
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewAngle, setPreviewAngle] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // API 호출 함수
        const fetchAnglePage = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await designApi.getAngleSelectPage(themeId);
                setAnglePage({ ...data });
                console.log("앵글 페이지 데이터 로드 성공:", data);
            } catch (err) {
                console.error("앵글 페이지 데이터 로드 실패:", err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchThemeAngleMappings = async () => {
            try {
                const mappings = await themeAngleMappingApi.getThemeAngleMappings(themeId, themePageId);
                setThemeAngleMappings(mappings);
                console.log("테마 앵글 매핑 로드 성공:", mappings);
            } catch (err) {
                console.error("테마 앵글 매핑 로드 실패:", err);
            }
        };

        const fetchAvailableAngles = async () => {
            try {
                // isPhoto가 true이거나 isVideo가 true인 앵글만 조회
                const angles = await themeAngleMappingApi.getAvailableAngles(themeId, {
                    isPhoto: true,
                    isVideo: true,
                    isGreeting: false
                });
                setAvailableAngles(angles);
                console.log("사용 가능한 앵글 로드 성공 (사진/동영상 타입만):", angles);
            } catch (err) {
                console.error("사용 가능한 앵글 로드 실패:", err);
            }
        };

        fetchAnglePage();
        fetchThemeAngleMappings();
        fetchAvailableAngles();
    }, [themeId]);

    // 위치 변경 핸들러
    const handlePositionChange = (type, axis, value) => {
        const numValue = value === "" ? 0 : parseInt(value);
        if (type === "leftButton") {
            setLeftButtonPosition((prev) => ({
                ...prev,
                [axis]: numValue,
            }));
        } else if (type === "rightButton") {
            setRightButtonPosition((prev) => ({
                ...prev,
                [axis]: numValue,
            }));
        }
    };

    // 방향 버튼 핸들러
    const handleDirectionClick = (type, direction) => {
        const position =
            type === "leftButton" ? leftButtonPosition : rightButtonPosition;
        const setPosition =
            type === "leftButton"
                ? setLeftButtonPosition
                : setRightButtonPosition;
        const step = 5;

        switch (direction) {
            case "up":
                setPosition({ ...position, y: position.y - step });
                break;
            case "down":
                setPosition({ ...position, y: position.y + step });
                break;
            case "left":
                setPosition({ ...position, x: position.x - step });
                break;
            case "right":
                setPosition({ ...position, x: position.x + step });
                break;
        }
    };

    // 중앙 정렬 핸들러
    const handleCenterAlign = (type) => {
        const centerX = 500;
        const centerY = 450;

        if (type === "leftButton") {
            setLeftButtonPosition({ x: centerX, y: centerY });
        } else if (type === "rightButton") {
            setRightButtonPosition({ x: centerX, y: centerY });
        }
    };

    // 체크박스 선택 핸들러
    const handleCheckboxChange = (id) => {
        setThemeAngleMappings((prevMappings) =>
            prevMappings.map((mapping) =>
                mapping.id === id
                    ? { ...mapping, selected: !mapping.selected }
                    : mapping
            )
        );
    };

    // 전체 선택 핸들러
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        setThemeAngleMappings((prevMappings) =>
            prevMappings.map((mapping) => ({ ...mapping, selected: !selectAll }))
        );
    };

    // 앵글 추가 핸들러
    const handleAddAngle = (e) => {
        e.preventDefault();
        setIsEditMode(false);
        setEditTargetMapping(null);
        setShowThemeAngleModal(true);
        document.body.classList.add("modal-open");
    };

    // 모달 닫기 핸들러
    const handleCloseModal = () => {
        setShowThemeAngleModal(false);
        setIsEditMode(false);
        setEditTargetMapping(null);
        document.body.classList.remove("modal-open");
    };

    // 새 앵글 저장 핸들러
    const handleSaveAngle = async (result) => {
        try {
            // 데이터 다시 로드
            const mappings = await themeAngleMappingApi.getThemeAngleMappings(themeId, themePageId);
            setThemeAngleMappings(mappings);
            
            // isPhoto가 true이거나 isVideo가 true인 앵글만 조회
            const angles = await themeAngleMappingApi.getAvailableAngles(themeId, {
                isPhoto: true,
                isVideo: true,
                isGreeting: false
            });
            setAvailableAngles(angles);
            
            alert("앵글이 성공적으로 추가/수정되었습니다.");
        } catch (error) {
            console.error('데이터 새로고침 실패:', error);
        }
    };

    // 앵글 삭제 핸들러
    const handleDeleteAngle = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
        document.body.classList.add("modal-open");
    };

    // 삭제 모달 닫기 핸들러
    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
        document.body.classList.remove("modal-open");
    };

    // 삭제 확인 핸들러
    const handleConfirmDelete = async () => {
        if (deleteTargetId) {
            try {
                await themeAngleMappingApi.removeAngleFromTheme(deleteTargetId);
                
                // 데이터 다시 로드
                const mappings = await themeAngleMappingApi.getThemeAngleMappings(themeId, themePageId);
                setThemeAngleMappings(mappings);
                
                // isPhoto가 true이거나 isVideo가 true인 앵글만 조회
                const angles = await themeAngleMappingApi.getAvailableAngles(themeId, {
                    isPhoto: true,
                    isVideo: true,
                    isGreeting: false
                });
                setAvailableAngles(angles);
                
                alert("앵글이 성공적으로 삭제되었습니다.");
                handleCloseDeleteModal();
            } catch (error) {
                console.error('앵글 삭제 실패:', error);
                alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
            }
        }
    };

    // 앵글 수정 핸들러
    const handleEditAngle = (mapping) => {
        setEditTargetMapping(mapping);
        setIsEditMode(true);
        setShowThemeAngleModal(true);
        document.body.classList.add("modal-open");
    };

    // 상세정보 모달 닫기 핸들러
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
    };

    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (mapping) => {
        setCurrentAngle(mapping);
        setShowDetailModal(true);
    };

    // 미리보기 모달 닫기 핸들러
    const handleClosePreviewModal = () => {
        setShowPreviewModal(false);
    };

    // 미리보기 아이콘 클릭 핸들러
    const handlePreviewClick = (angle) => {
        setPreviewAngle(angle);
        setShowPreviewModal(true);
    };

    // 테이블 초기화
    useEffect(() => {
        // 테이블 초기화
        if (window.tableManager) {
            window.tableManager.initTable({
                tableSelector: ".common-table",
                filterSelector: "#angle-filter",
                columnWidthOptions: {
                    equalWidth: false,
                    firstColumnWidth: 5,
                    lastColumnWidth: 5,
                    fixedColumns: {
                        1: 10, // NO 열
                        2: 20, // 앵글명 열
                        3: 15, // 앵글 타입 열
                        4: 10, // 미리보기 열
                        5: 10, // 상세정보보 열
                        6: 10, // 수정 열
                        7: 10, // 삭제 열
                    },
                },
            });
        }
    }, []);

    // 리소스 업로드 처리 함수
    const handleResourceUploaded = (resourceType, resourceId) => {
        setAnglePage({
            ...anglePage,
            [resourceType]: resourceId
        });
    };

    // 입력값 변경 핸들러
    const handleInputChange = (field, value) => {
        setAnglePage(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const handleTimerDecrease = () => {
        setAnglePage(prev => ({
            ...prev,
            timerSeconds: Math.max((prev.timerSeconds || 10) - 1, 1)
        }));
    };

    const handleTimerIncrease = () => {
        setAnglePage(prev => ({
            ...prev,
            timerSeconds: (prev.timerSeconds || 10) + 1
        }));
    };

    // 자식 컴포넌트(예: StartSetting)에서 이벤트 리스너 추가
    useEffect(() => {
        const eventName = `savePageDesign_angle`; // 각 컴포넌트마다 고유한 이벤트 이름

        const saveHandler = async (event) => {
            try {
                // 저장 중임을 표시
                setIsLoading(true);

                // 자체 로직을 사용하여 데이터 저장
                const isSucceed = await designApi.saveAngleSelectPage(event.detail.themeId, anglePage);

                if(isSucceed) {
                    // 성공 메시지
                    alert("저장되었습니다.");
                } else {
                    // 실패 메시지
                    alert("실패 구현 필요.");
                }

            } catch (error) {
                // 오류 처리
                console.error("저장 실패:", error);
                alert("저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        // 이벤트 리스너 등록
        document.addEventListener(eventName, saveHandler);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [anglePage, themeId]); // 의존성 배열에 필요한 상태 포함

    return (
        <div className="design-settings">
            {/* 언어 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">언어 선택</h4>
                <div className="option-select-container">
                    <select
                        className="option-select"
                        value={anglePage.languageCode || "ko"}
                        onChange={(e) =>
                            handleInputChange('languageCode', e.target.value)
                        }
                    >
                        <option value="ko">한국어</option>
                        <option value="en">영어</option>
                        <option value="jp">일본어</option>
                        <option value="cn">중국어</option>
                    </select>
                    <i className="ri-arrow-down-s-line select-arrow"></i>
                </div>
            </div>

            {/* 배경 이미지 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 이미지</h4>
                <ResourceUploadField
                    label=""
                    resourceId={anglePage.backgroundResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundResourceId", id)
                    }
                    acceptTypes="image/*"
                />
            </div>

            {/* 배경 음악 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">배경 음악</h4>
                <ResourceUploadField
                    label=""
                    resourceId={anglePage.backgroundMusicResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("backgroundMusicResourceId", id)
                    }
                    acceptTypes="audio/*"
                />
            </div>

            {/* 폰트 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">폰트</h4>
                <ResourceUploadField
                    label=""
                    resourceId={anglePage.fontResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("fontResourceId", id)
                    }
                    acceptTypes=".ttf,.otf,.woff,.woff2"
                />
            </div>

            {/* 문구 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">문구 (사진/동영상 문구 색상 포함)</h4>
                <div className="settings-row">
                    <div className="settings-input">
                        <input
                            type="text"
                            placeholder="문구를 입력해주세요"
                            className="text-input"
                            value={anglePage.messageText || ""}
                            onChange={(e) => handleInputChange('messageText', e.target.value)}
                        />
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">색상</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={anglePage.messageColor || "#FFFFFF"}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={anglePage.messageColor || "#FFFFFF"}
                            onChange={(e) => handleInputChange('messageColor', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* 효과음 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">효과음</h4>
                <ResourceUploadField
                    label="버튼클릭"
                    resourceId={anglePage.buttonClickSoundResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("buttonClickSoundResourceId", id)
                    }
                    acceptTypes="audio/*"
                />
                <ResourceUploadField
                    label="에러효과음"
                    resourceId={anglePage.errorSoundResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("errorSoundResourceId", id)
                    }
                    acceptTypes="audio/*"
                />
            </div>

            {/* 라디오 버튼 색상 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">라디오 버튼 색상 (앵글버튼 하단)</h4>
                <div className="settings-row">
                    <div className="settings-label">비활성</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={anglePage.radioInactiveColor || "#c7c7c7"}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={anglePage.radioInactiveColor || "#c7c7c7"}
                            onChange={(e) => handleInputChange('radioInactiveColor', e.target.value)}
                        />
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">활성</div>
                    <div className="color-picker-container">
                        <input
                            type="text"
                            className="color-input"
                            value={anglePage.radioActiveColor || "#FFFFFF"}
                            readOnly
                        />
                        <input
                            type="color"
                            className="color-picker"
                            value={anglePage.radioActiveColor || "#FFFFFF"}
                            onChange={(e) => handleInputChange('radioActiveColor', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* 페이지 이동 버튼 (좌) 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">페이지 이동 버튼 (좌)</h4>
                <ResourceUploadField
                    label=""
                    resourceId={anglePage.leftNavigationButtonResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("leftNavigationButtonResourceId", id)
                    }
                    acceptTypes="image/*"
                />
                <div className="settings-row">
                    <div className="settings-label">위치</div>
                    <div className="position-component">
                        <div className="position-input-group">
                            <span className="position-label">X</span>
                            <input
                                type="text"
                                className="position-input"
                                id="left-btn-x"
                                value={anglePage.leftNavigationButtonPositionX || 0}
                                onChange={(e) => handlePositionChange('leftButton', 'positionX', e.target.value)}
                            />
                            <span className="position-label">Y</span>
                            <input
                                type="text"
                                className="position-input"
                                id="left-btn-y"
                                value={anglePage.leftNavigationButtonPositionY || 0}
                                onChange={(e) => handlePositionChange('leftButton', 'positionY', e.target.value)}
                            />
                        </div>
                        <div className="direction-buttons">
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="up"
                                    onClick={() => handleDirectionClick('leftButton', 'up')}
                                >
                                    <i className="ri-arrow-up-s-fill"></i>
                                </button>
                            </div>
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="left"
                                    onClick={() => handleDirectionClick('leftButton', 'left')}
                                >
                                    <i className="ri-arrow-left-s-fill"></i>
                                </button>
                                <button
                                    className="direction-btn"
                                    data-direction="right"
                                    onClick={() => handleDirectionClick('leftButton', 'right')}
                                >
                                    <i className="ri-arrow-right-s-fill"></i>
                                </button>
                            </div>
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="down"
                                    onClick={() => handleDirectionClick('leftButton', 'down')}
                                >
                                    <i className="ri-arrow-down-s-fill"></i>
                                </button>
                            </div>
                        </div>
                        <button
                            className="btn center-btn"
                            onClick={() => handleCenterAlign('leftButton')}
                        >
                            중앙정렬
                        </button>
                    </div>
                </div>
            </div>

            {/* 페이지 이동 버튼 (우) 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">페이지 이동 버튼 (우)</h4>
                <ResourceUploadField
                    label=""
                    resourceId={anglePage.rightNavigationButtonResourceId}
                    onResourceUploaded={(id) =>
                        handleResourceUploaded("rightNavigationButtonResourceId", id)
                    }
                    acceptTypes="image/*"
                />
                <div className="settings-row">
                    <div className="settings-label">위치</div>
                    <div className="position-component">
                        <div className="position-input-group">
                            <span className="position-label">X</span>
                            <input
                                type="text"
                                className="position-input"
                                id="right-btn-x"
                                value={anglePage.rightNavigationButtonPositionX || 0}
                                onChange={(e) => handlePositionChange('rightButton', 'positionX', e.target.value)}
                            />
                            <span className="position-label">Y</span>
                            <input
                                type="text"
                                className="position-input"
                                id="right-btn-y"
                                value={anglePage.rightNavigationButtonPositionY || 0}
                                onChange={(e) => handlePositionChange('rightButton', 'positionY', e.target.value)}
                            />
                        </div>
                        <div className="direction-buttons">
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="up"
                                    onClick={() => handleDirectionClick('rightButton', 'up')}
                                >
                                    <i className="ri-arrow-up-s-fill"></i>
                                </button>
                            </div>
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="left"
                                    onClick={() => handleDirectionClick('rightButton', 'left')}
                                >
                                    <i className="ri-arrow-left-s-fill"></i>
                                </button>
                                <button
                                    className="direction-btn"
                                    data-direction="right"
                                    onClick={() => handleDirectionClick('rightButton', 'right')}
                                >
                                    <i className="ri-arrow-right-s-fill"></i>
                                </button>
                            </div>
                            <div className="direction-row">
                                <button
                                    className="direction-btn"
                                    data-direction="down"
                                    onClick={() => handleDirectionClick('rightButton', 'down')}
                                >
                                    <i className="ri-arrow-down-s-fill"></i>
                                </button>
                            </div>
                        </div>
                        <button
                            className="btn center-btn"
                            onClick={() => handleCenterAlign('rightButton')}
                        >
                            중앙정렬
                        </button>
                    </div>
                </div>
            </div>

            {/* 앵글 타입 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">앵글 타입 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">사진</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={anglePage.enablePhotoAngle || false}
                                onChange={(e) => handleInputChange('enablePhotoAngle', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">동영상</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={anglePage.enableVideoAngle || false}
                                onChange={(e) => handleInputChange('enableVideoAngle', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>

            {/* 타이머 설정 섹션 */}
            {/* <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button
                            className="timer-btn"
                            onClick={handleTimerDecrease}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input
                            type="text"
                            className="timer-input"
                            value={anglePage.timerSeconds || 10}
                            readOnly
                        />
                        <button
                            className="timer-btn"
                            onClick={handleTimerIncrease}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select
                            className="page-select"
                            value={anglePage.timerTimeoutRedirectUrl || "start"}
                            onChange={(e) => handleInputChange('timerTimeoutRedirectUrl', e.target.value)}
                        >
                            <option value="start">시작화면</option>
                            <option value="layout">레이아웃</option>
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div> */}

            {/* 앵글 데이터 섹션 헤더 */}
            <div className="data-section-header">
                <div className="data-section-title">
                    <h4 className="section-title">앵글</h4>
                    <div className="section-title-icon">
                        <a
                            href="#"
                            className="angle-add-btn"
                            title="앵글 추가"
                            onClick={handleAddAngle}
                        >
                            <i className="ri-add-circle-line"></i>
                        </a>
                    </div>
                </div>
                <div className="filter-dropdown-container">
                    <div className="filter-dropdown">
                        <select
                            id="angle-filter"
                            value={angleFilter}
                            onChange={(e) => setAngleFilter(e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="photo">사진</option>
                            <option value="video">동영상</option>
                            <option value="combined">동영상 + 사진</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* 데이터 테이블 섹션 */}
            <div className="common-table-container">
                <table className="common-table">
                    <thead>
                    <tr>
                        <th>
                            <div className="checkbox-container">
                                <input
                                    type="checkbox"
                                    id="select-all"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                />
                                <label htmlFor="select-all" className="custom-checkbox"></label>
                            </div>
                        </th>
                        <th>NO</th>
                        <th>앵글명</th>
                        <th>앵글 타입</th>
                        <th>상세정보</th>
                        <th>수정</th>
                        <th>삭제</th>
                    </tr>
                    </thead>
                    <tbody>
                    {themeAngleMappings.map((mapping) => (
                        <tr key={mapping.id}>
                            <td>
                                <div className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        id={`mapping-${mapping.id}`}
                                        checked={mapping.selected || false}
                                        onChange={() => handleCheckboxChange(mapping.id)}
                                    />
                                    <label htmlFor={`mapping-${mapping.id}`} className="custom-checkbox"></label>
                                </div>
                            </td>
                            <td>{mapping.displayOrder}</td>
                            <td>{mapping.angleName}</td>
                            <td>{mapping.formattedAngleType || mapping.angleTypeDescription}</td>
                            <td><button className="info-btn" onClick={() => handleInfoClick(mapping)}><i className="ri-information-line"></i></button></td>
                            <td><button className="edit-btn" onClick={() => handleEditAngle(mapping)}><i className="ri-pencil-line"></i></button></td>
                            <td><button className="delete-btn" onClick={() => handleDeleteAngle(mapping.id)}><i className="ri-close-line"></i></button></td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* 테마 앵글 선택 모달 */}
            {showThemeAngleModal && (
                <ThemeAngleSelectionModal
                    themePageMaster={themePageMaster}
                    themeId={themeId}
                    availableAngles={availableAngles}
                    onClose={handleCloseModal}
                    onSave={handleSaveAngle}
                    isEditMode={isEditMode}
                    initialData={editTargetMapping}
                />
            )}

            {/* 삭제 확인 모달 */}
            {showDeleteModal && (
                <div className="modal-overlay active">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">앵글 삭제</h3>
                            <button className="modal-close" onClick={handleCloseDeleteModal}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>이 앵글을 삭제하시겠습니까?</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCloseDeleteModal}
                            >
                                취소
                            </button>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleConfirmDelete}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* 상세정보 모달 */}
            {showDetailModal && currentAngle && (
                <div className="modal-overlay active" id="angle-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">앵글 상세정보</h3>
                            <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">앵글명</span>
                                <span className="detail-content">{currentAngle.angleName}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">타입</span>
                                <span className="detail-content">{currentAngle.angleTypeDescription}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">표시 순서</span>
                                <span className="detail-content">{currentAngle.displayOrder}</span>
                            </div>
                            {currentAngle.angleDataResourceId && (
                                <div className="detail-item-container">
                                    <span className="detail-label">앵글 데이터</span>
                                    <span className="detail-content">{currentAngle.angleDataResourceId}</span>
                                </div>
                            )}
                            <div className="detail-item-container">
                                <div className="detail-row">
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 기본 이미지</span>
                                        <div className="modal-data-image">
                                            {currentAngle.customDefaultImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentAngle.customDefaultImageResourceId}`}
                                                    alt="custom_default"
                                                    onError={(e) => {
                                                        e.target.src = "images/angleData/angle_data.png";
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src="images/angleData/angle_data.png"
                                                    alt="default_image"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 호버 이미지</span>
                                        <div className="modal-data-image">
                                            {currentAngle.customHoverImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentAngle.customHoverImageResourceId}`}
                                                    alt="custom_hover"
                                                    onError={(e) => {
                                                        e.target.src = "images/angleData/angle_data2.png";
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src="images/angleData/angle_data2.png"
                                                    alt="default_hover"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleCloseDetailModal}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AngleSetting;
